/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { SysqueCheckBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-check-box-vm';
import { ANNOTATION_SETTINGS_FACADE_TOKEN, IAnnotationSettingsFacade } from "../../../../../../../../../../data-access/src/lib/domain/facades-models/annotation-settings-facade-interface";
import { GlobalConstants } from "../../../../../../../constants/global-constants";
import { Annotation } from 'libs/sysque/toolbelt/feature/src/lib/models/annotation-settings';
import { Subscription } from 'rxjs';

@Component({
  selector: 'cable-tray-sysque-item-number-tag',
  templateUrl: './cable-tray-item-number-tag.component.html',
  styleUrls: ['./cable-tray-item-number-tag.component.scss'],
  standalone: false
})
export class CableTrayItemNumberTagComponent implements OnInit, OnDestroy {
  constructor(@Inject(ANNOTATION_SETTINGS_FACADE_TOKEN) private annotationSettingsFacade: IAnnotationSettingsFacade) { }

  itemNumberDataFromXml: Annotation;
  cableTrayItemNumberDataSelection: Annotation;
  selectedPathSubscription: Subscription;
  selectedFittingsPathSubscription: Subscription;

  //Placement initialization
  placementLabel = GlobalConstants.PLACEMENT_LABEL;
  placementPlaceholder = GlobalConstants.DEFAULT;
  placementSelectedValue = '';
  placementItems: string[] = [
    GlobalConstants.ABOVE,
    GlobalConstants.CENTER,
    GlobalConstants.BELOW
  ];

  //Offset initialization
  propertiesLabel = GlobalConstants.PROPERTIES_LABEL;
  propertiesItems: SysqueCheckBoxVM[] = [];

  //Location initialization
  locationLabel = GlobalConstants.ITEM_NUMBER_LOCATION_LABEL;
  locationPlaceholder = '';
  locationPath = '';

  //Fitting location initialization
  fittingLocationLabel = GlobalConstants.ITEM_NUMBER_FITTINGS_LOCATION_LABEL;
  fittingLocationPlaceholder = '';
  fittingLocationPath = '';

  public ngOnInit(): void {
    //retrieve data from xml file
    this.itemNumberDataFromXml = this.annotationSettingsFacade.getTagDataFromXmlByTabNameAndAnnotationName(GlobalConstants.CABLE_TRAY_TAB_NAME, GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME);

    //retrieve selection data from local storage
    this.cableTrayItemNumberDataSelection = this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME);

    //if there is a selection data from local storage this means that it wasn't the first load for the annotation settings dialog 
    //and the user can have a previous selection and we don't want to lose this selection
    //and load it
    if (this.cableTrayItemNumberDataSelection !== null) {
      this.loadData(this.cableTrayItemNumberDataSelection);
    } else {
      //else load data from xml file
      this.loadData(this.itemNumberDataFromXml);

      //there wasn't a selection yet so we need to save the default selection to the local storage in order to set each property when the user start to make a selection
      this.annotationSettingsFacade.setDefaultSelectionToStorageByTabNameAndAnnotationName(this.itemNumberDataFromXml, GlobalConstants.CABLE_TRAY_TAB_NAME, GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME,
        GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME);
    }
  }

  public loadData(data: Annotation): void {

    //Placement value initialization from xml file
    this.placementSelectedValue = data.location || this.placementItems[0];

    //Properties initialization from xml file
    this.propertiesItems = [
      { label: GlobalConstants.SHOW_LEADER, isChecked: this.annotationSettingsFacade.getPropertiesPropertyByName(GlobalConstants.SHOW_LEADER, data) },
      { label: GlobalConstants.HORIZONTAL_TEXT, isChecked: this.annotationSettingsFacade.getPropertiesPropertyByName(GlobalConstants.HORIZONTAL_TEXT, data) },
      { label: GlobalConstants.SHOW_BORDER, isChecked: this.annotationSettingsFacade.getPropertiesPropertyByName(GlobalConstants.SHOW_BORDER, data) },
    ];

    //Location initialization from xml file
    this.locationPath = data.tagPath;

    //Fitting Location initialization from xml file
    this.fittingLocationPath = data.fittingTagPath;

     //load image and labels
     const tagsState = this.annotationSettingsFacade.getTagsStateFromStorageByTabName(GlobalConstants.CABLE_TRAY_TAB_NAME);
     this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CABLE_TRAY_TAB_NAME, tagsState, [data]);

  }

  public onPlacementValueChanged(item: string): void {
    this.annotationSettingsFacade.setPlacementValueToStorage(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME, item);
    //load image and labels
    this.loadImage();
  }

  public onPropertiesValueChanged(item: SysqueCheckBoxVM): void {
    this.annotationSettingsFacade.setPropertiesValueToStorage(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME, item.label, item.isChecked);
  }

  public onLocationValueChanged(item: string): void {
    this.annotationSettingsFacade.setTagPathValueToStorage(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME, item);
  }

  public onFittingsLocationValueChanged(item: string): void {
    this.annotationSettingsFacade.setFittingTagPathValueToStorage(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME, item);
  }

  public getTagLocationPath(): void {
    this.selectedPathSubscription = this.annotationSettingsFacade.getTagLocationPath().subscribe((data) => {
      this.locationPath = data;
    });
  }

  public getFittingsTagLocationPath(): void {
    this.selectedFittingsPathSubscription = this.annotationSettingsFacade.getFittingsTagLocationPath().subscribe((data) => {
      this.fittingLocationPath = data;
    });
  }

  public ngOnDestroy(): void {
    if (this.selectedPathSubscription) {
      this.selectedPathSubscription.unsubscribe();
    }
    if (this.selectedFittingsPathSubscription) {
      this.selectedFittingsPathSubscription.unsubscribe();
    }
  }

  public loadImage(): void{
    const tagsState = this.annotationSettingsFacade.getTagsStateFromStorageByTabName(GlobalConstants.CABLE_TRAY_TAB_NAME);
    const selectionData = this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME);
    this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CABLE_TRAY_TAB_NAME, tagsState, [selectionData]);
  }
}
